/**
 * A discount or buffer (F9), set by the office on a job or a quote.
 *
 * Kind (none / % / $), direction (discount / buffer), the number and the
 * office's own words. A percentage is held in basis points to one decimal, a
 * dollar amount in cents; one or the other, never both. The server does the
 * arithmetic and the cap — this only turns what was typed into the two numbers.
 */
import { useEffect, useState } from "react";

import { ApiError } from "../lib/api";

export type Adjustment = { bp: number | null; cents: number | null; label: string | null };

type Kind = "none" | "percent" | "dollars";
type Direction = "discount" | "buffer";

export const MAX_ADJUSTMENT_PERCENT = 50;
export const MAX_LABEL = 60;

/** What was typed, as the numbers to send; or the sentence to show when it can't be. */
export function parseAdjustment(
  kind: Kind,
  direction: Direction,
  amount: string,
  label: string,
): { ok: true; value: Adjustment } | { ok: false; error: string } {
  if (kind === "none") return { ok: true, value: { bp: null, cents: null, label: null } };
  const words = label.trim();
  if (words.length > MAX_LABEL) return { ok: false, error: `The label can be up to ${MAX_LABEL} characters.` };
  const n = amount.trim() === "" ? NaN : Number(amount);
  const sign = direction === "discount" ? -1 : 1;
  if (kind === "percent") {
    if (!Number.isFinite(n) || n <= 0 || n > MAX_ADJUSTMENT_PERCENT) {
      return { ok: false, error: `A percentage has to be above 0 and no more than ${MAX_ADJUSTMENT_PERCENT}.` };
    }
    // One decimal place: 12.5% is 1250 basis points.
    return { ok: true, value: { bp: sign * Math.round(n * 10) * 10, cents: null, label: words || null } };
  }
  if (!Number.isFinite(n) || n <= 0) return { ok: false, error: "A dollar amount has to be more than $0." };
  const cents = Math.round(n * 100);
  if (cents < 1) return { ok: false, error: "A dollar amount has to be at least 1 cent." };
  return { ok: true, value: { bp: null, cents: sign * cents, label: words || null } };
}

function startState(bp: number | null | undefined, cents: number | null | undefined) {
  if (cents !== null && cents !== undefined) {
    return {
      kind: "dollars" as Kind,
      direction: (cents < 0 ? "discount" : "buffer") as Direction,
      amount: (Math.abs(cents) / 100).toFixed(2),
    };
  }
  if (bp) {
    return {
      kind: "percent" as Kind,
      direction: (bp < 0 ? "discount" : "buffer") as Direction,
      amount: String(Math.abs(bp) / 100),
    };
  }
  return { kind: "none" as Kind, direction: "discount" as Direction, amount: "" };
}

export function AdjustmentEditor({
  bp,
  cents,
  label,
  idPrefix,
  onSave,
}: {
  bp: number | null | undefined;
  cents: number | null | undefined;
  label: string | null | undefined;
  idPrefix: string;
  /** Save it; throw to have the message shown here. */
  onSave: (adjustment: Adjustment) => Promise<void>;
}) {
  const [state, setState] = useState(() => startState(bp, cents));
  const [words, setWords] = useState(label ?? "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // What is saved is the truth: follow it when it changes under us.
  useEffect(() => {
    setState(startState(bp, cents));
    setWords(label ?? "");
  }, [bp, cents, label]);

  async function save() {
    const parsed = parseAdjustment(state.kind, state.direction, state.amount, words);
    if (!parsed.ok) {
      setError(parsed.error);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await onSave(parsed.value);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Couldn't save the discount or buffer.");
    } finally {
      setBusy(false);
    }
  }

  const active = state.kind !== "none";

  return (
    <div className="adjust-edit">
      <h3 className="bill-head">Discount / buffer</h3>
      <div className="adjust-fields">
        <label>
          <span className="tiny muted">Type</span>
          <select
            value={state.kind}
            disabled={busy}
            aria-label="Discount or buffer type"
            onChange={(e) => setState({ ...state, kind: e.target.value as Kind })}
          >
            <option value="none">None</option>
            <option value="percent">%</option>
            <option value="dollars">$</option>
          </select>
        </label>
        {active && (
          <>
            <label>
              <span className="tiny muted">Direction</span>
              <select
                value={state.direction}
                disabled={busy}
                aria-label="Discount or buffer"
                onChange={(e) => setState({ ...state, direction: e.target.value as Direction })}
              >
                <option value="discount">Discount</option>
                <option value="buffer">Buffer</option>
              </select>
            </label>
            <label>
              <span className="tiny muted">{state.kind === "percent" ? "Percent" : "Dollars"}</span>
              <input
                id={`${idPrefix}-amount`}
                type="number"
                inputMode="decimal"
                min="0"
                step={state.kind === "percent" ? "0.1" : "0.01"}
                value={state.amount}
                disabled={busy}
                aria-label={state.kind === "percent" ? "Percent" : "Dollars"}
                onChange={(e) => setState({ ...state, amount: e.target.value })}
              />
            </label>
            <label className="adjust-label">
              <span className="tiny muted">Label (optional)</span>
              <input
                type="text"
                maxLength={MAX_LABEL}
                value={words}
                disabled={busy}
                aria-label="Label"
                onChange={(e) => setWords(e.target.value)}
              />
            </label>
          </>
        )}
        <button type="button" className="btn adjust-save" disabled={busy} onClick={() => void save()}>
          {busy ? "Saving…" : "Save"}
        </button>
      </div>
      {error && (
        <p className="notice notice-crit" role="alert" style={{ marginTop: 8 }}>
          {error}
        </p>
      )}
    </div>
  );
}
